import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Cloud, Database, Zap, Code, Rocket, Target, Lightbulb, Star, 
    BrainCircuit, Blocks, WandSparkles, Briefcase, Braces, Binary, ChartScatter,
    Terminal, GitBranch, QrCode } from 'lucide-react';

const baseIcons = [
  { Icon: Cloud, color: '#60a5fa', size: 50 },
  { Icon: Database, color: '#a78bfa', size: 48 },
  { Icon: Zap, color: '#fbbf24', size: 52 },
  { Icon: Code, color: '#34d399', size: 46 },
  { Icon: Rocket, color: '#fb923c', size: 45 },
  { Icon: Target, color: '#f472b6', size: 44 },
  { Icon: Lightbulb, color: '#22d3ee', size: 48 },
  { Icon: Star, color: '#818cf8', size: 50 },
  { Icon: BrainCircuit, color: '#8b5cf6', size: 48 },
  { Icon: Blocks, color: '#10b981', size: 46 },
  { Icon: WandSparkles, color: '#ec4899', size: 45 },
  { Icon: Briefcase, color: '#f59e0b', size: 44 },
  { Icon: Braces, color: '#10b981', size: 46 },
  { Icon: Binary, color: '#ec4899', size: 45 },
  { Icon: ChartScatter, color: '#8b5cf6', size: 48 },
  { Icon: Terminal, color: '#22d3ee', size: 48 },
  { Icon: GitBranch, color: '#818cf8', size: 50 },
  { Icon: QrCode, color: '#22d3ee', size: 48 },
];

// Function to generate random positions between -40% and +40%
function getRandomPosition() {
  const x = Math.floor(Math.random() * 81) - 40; // -40 to 40
  const y = Math.floor(Math.random() * 81) - 40; // -40 to 40
  return { x, y };
}

interface FloatingIconsProps {
  className?: string;
}

function FloatingIcons({ className = '' }: FloatingIconsProps) {
  // Generate randomized positions once on mount
  const floatingIcons = useMemo(() => {
    return baseIcons.map((icon, index) => ({
      ...icon,
      delay: index * 0.3,
      ...getRandomPosition()
    }));
  }, []);

  return (
    <div className={`floating-icons-container ${className}`}>
      {floatingIcons.map((iconConfig, index) => (
        <motion.div
          key={index}
          className="hero-icon-wrapper"
          style={{
            left: `${50 + iconConfig.x}%`,
            top: `${50 + iconConfig.y}%`,
          }}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ 
            opacity: [0, 0.25, 0.25, 0],
            scale: [0.8, 1, 1, 0.8]
          }}
          transition={{
            opacity: { 
              delay: iconConfig.delay, 
              duration: 4, 
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 2
            },
            scale: { 
              delay: iconConfig.delay, 
              duration: 4, 
              ease: "easeInOut",
              repeat: Infinity,
              repeatDelay: 2
            }
          }}
        >
          {/* Outer animation for floating movement */}
          <motion.div
            animate={{ 
              y: [0, -8, 0],
              x: [0, 5, 0]
            }}
            transition={{
              delay: iconConfig.delay,
              duration: 6,
              repeat: Infinity,
              ease: "easeInOut",
              repeatType: "reverse"
            }}
          >
            {/* Inner animation for rotation and scale */}
            <motion.div
              animate={{ 
                rotate: [0, 3, -3, 3, 0],
                scale: [1, 1.05, 1]
              }}
              transition={{
                rotate: {
                  duration: 8,
                  repeat: Infinity,
                  ease: "easeInOut"
                },
                scale: {
                  duration: 4,
                  repeat: Infinity,
                  ease: "easeInOut"
                }
              }}
            >
              <iconConfig.Icon
                size={iconConfig.size}
                style={{
                  color: iconConfig.color,
                  filter: 'drop-shadow(0 4px 6px rgba(0, 0, 0, 0.15))'
                }}
              />
            </motion.div>
          </motion.div>
        </motion.div>
      ))}
    </div>
  );
}

export default FloatingIcons;

